import { classifyValue, isProvenanceKey, scalarText, statusTone, tableColumns } from '@/lib/registryContent';

// Generic recursive viewer: objects → labelled sections, arrays of objects →
// tables, flat scalar maps → 2-col lookup tables, scalar arrays → lists,
// scalars → text (valid/invalid style values as pills). Underscore keys
// (_doc/_meta) render as muted provenance, not data.
export function RegistryContent({ value, depth = 0 }: { value: unknown; depth?: number }) {
  return <div className="space-y-4"><Node value={value} depth={depth} /></div>;
}

function ScalarValue({ v }: { v: unknown }) {
  const tone = statusTone(v);
  if (tone) {
    return (
      <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${tone === 'good' ? 'bg-success-soft text-success' : 'bg-flagged-soft text-flagged'}`}>
        {scalarText(v)}
      </span>
    );
  }
  return <span className="font-mono text-sm">{scalarText(v)}</span>;
}

// A flat scalar map as a dashboard-style tile (key label + value), tinted by
// valid/invalid tone so the matrix scans at a glance.
function MapTile({ label, value }: { label: string; value: unknown }) {
  const tone = statusTone(value);
  const tile = tone === 'good'
    ? 'border-success bg-success-soft'
    : tone === 'bad'
      ? 'border-flagged bg-flagged-soft'
      : 'border-border bg-surface-sunken';
  const val = tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-flagged' : 'text-text-primary';
  return (
    <div className={`min-w-[128px] flex-1 rounded border p-13 ${tile}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{label}</div>
      <div className={`mt-1 font-mono text-body-sm font-semibold ${val}`}>{scalarText(value)}</div>
    </div>
  );
}

function Node({ value, depth }: { value: unknown; depth: number }) {
  const kind = classifyValue(value);
  if (kind === 'empty') return <span className="text-text-muted">—</span>;
  if (kind === 'scalar') return <ScalarValue v={value} />;
  if (kind === 'scalar-array') {
    return (
      <ul className="list-disc pl-5 text-sm">
        {(value as unknown[]).map((v, i) => <li key={i} className="font-mono">{scalarText(v)}</li>)}
      </ul>
    );
  }
  if (kind === 'scalar-map') {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      <div className="flex flex-wrap gap-8">
        {entries.map(([k, v]) => <MapTile key={k} label={k} value={v} />)}
      </div>
    );
  }
  if (kind === 'object-array') {
    const rows = value as Array<Record<string, unknown>>;
    const cols = tableColumns(rows);
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border border-border text-sm">
          <thead className="bg-surface-sunken">
            <tr>{cols.map((c) => <th key={c} className="border-b border-border px-2 py-1 text-left font-medium">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="odd:bg-surface even:bg-surface-sunken">
                {cols.map((c) => <td key={c} className="border-b border-border px-2 py-1 align-top"><ScalarValue v={r[c]} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // object
  const entries = Object.entries(value as Record<string, unknown>);
  const HeadingTag: 'h2' | 'h3' = depth === 0 ? 'h2' : 'h3';
  return (
    <div className="space-y-3">
      {entries.map(([key, v]) => (
        <section key={key} className={depth === 0 ? 'rounded border border-border bg-surface p-4' : ''}>
          <HeadingTag className={depth === 0 ? 'mb-2 text-lg font-semibold' : 'mb-1 text-sm font-semibold text-text-primary'}>
            {key}
          </HeadingTag>
          {isProvenanceKey(key)
            ? <p className="text-xs italic text-text-secondary">{scalarText(v)}</p>
            : <Node value={v} depth={depth + 1} />}
        </section>
      ))}
    </div>
  );
}
